import { useEffect, useState } from 'react';
import { HiOutlineDocumentText, HiOutlineArrowTopRightOnSquare, HiOutlineArrowDownTray } from "react-icons/hi2";
import Button from "../../../components/button/button";
import { getInvoicesResponse } from '../../../services/billing';
import { SectionCard, Pill, Notice, LoadingState, EmptyState } from "../components";

// invoices come grouped by status from billing/bff/invoices
const STATUSES = [
    { key: 'open', label: 'Open', tone: 'warning' },
    { key: 'paid', label: 'Paid', tone: 'success' },
    { key: 'uncollectible', label: 'Uncollectible', tone: 'danger' },
];
const STATUS_BY_KEY = Object.fromEntries(STATUSES.map(status => [status.key, status]));

function FilterChip({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                active
                    ? 'border-primary bg-primary text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
            }`}
        >
            {children}
        </button>
    );
}

function InvoiceTable({ invoices }) {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        <th scope="col" className="px-5 py-3">Invoice</th>
                        <th scope="col" className="px-5 py-3">Status</th>
                        <th scope="col" className="px-5 py-3">Created</th>
                        <th scope="col" className="px-5 py-3 text-right">Amount</th>
                        <th scope="col" className="px-5 py-3"><span className="sr-only">Actions</span></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {invoices.map(invoice => {
                        const status = STATUS_BY_KEY[invoice.status] || { label: invoice.status, tone: 'neutral' };
                        return (
                            <tr key={invoice.invoiceNumber} className="hover:bg-gray-50/60">
                                <td className="whitespace-nowrap px-5 py-3 font-medium text-gray-900">{invoice.invoiceNumber || '–'}</td>
                                <td className="whitespace-nowrap px-5 py-3"><Pill tone={status.tone}>{status.label}</Pill></td>
                                <td className="whitespace-nowrap px-5 py-3 text-gray-500">{invoice.created}</td>
                                <td className="whitespace-nowrap px-5 py-3 text-right font-medium text-gray-900">
                                    {/* paid invoices show what was paid; open ones what is due */}
                                    {invoice.status === 'paid' ? invoice.amount : invoice.due}
                                </td>
                                <td className="whitespace-nowrap px-5 py-3">
                                    <div className="flex items-center justify-end gap-1">
                                        {invoice.hostedInvoiceUrl &&
                                            <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer" title={invoice.status === 'open' ? 'View and pay' : 'View invoice'}
                                               className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                                                <HiOutlineArrowTopRightOnSquare className="h-4 w-4" />
                                                {invoice.status === 'open' ? 'Pay' : 'View'}
                                            </a>}
                                        {invoice.invoicePdf &&
                                            <a href={invoice.invoicePdf} target="_blank" rel="noreferrer" title="Download PDF"
                                               className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900">
                                                <HiOutlineArrowDownTray className="h-4 w-4" />
                                            </a>}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default function Index() {
    const [invoicesData, setInvoicesData] = useState({});
    const [loadState, setLoadState] = useState({ loading: true, failed: false });
    const [filter, setFilter] = useState('all');

    async function fetchData() {
        const { response, status } = await getInvoicesResponse();
        if (status === 200) {
            setInvoicesData(response || {});
            setLoadState({ loading: false, failed: false });
        } else {
            setLoadState({ loading: false, failed: true });
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    if (loadState.loading) {
        return <LoadingState text="Loading invoices..." />;
    }
    if (loadState.failed) {
        return <Notice tone="danger" title="Unable to load invoices"
                       action={<Button text="Retry" color="default" onClick={() => { setLoadState({ loading: true }); fetchData(); }} />} />;
    }

    const counts = Object.fromEntries(STATUSES.map(status => [status.key, (invoicesData[status.key] || []).length]));
    const all = STATUSES.flatMap(status => invoicesData[status.key] || []);
    const invoices = filter === 'all' ? all : (invoicesData[filter] || []);
    const openCount = counts.open || 0;

    return (
        <div className="flex flex-col gap-6">
            {openCount > 0 &&
                <Notice tone="warning" title={`${openCount} open invoice${openCount === 1 ? '' : 's'}`}>
                    Pay open invoices to keep your subscription active.
                </Notice>
            }

            <SectionCard
                title="Invoices"
                description="Your most recent invoices. Open one to view or pay it on Stripe."
                action={all.length > 0 &&
                    <div className="flex flex-wrap gap-1.5">
                        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>All · {all.length}</FilterChip>
                        {STATUSES.filter(status => counts[status.key] > 0).map(status =>
                            <FilterChip key={status.key} active={filter === status.key} onClick={() => setFilter(status.key)}>
                                {status.label} · {counts[status.key]}
                            </FilterChip>
                        )}
                    </div>
                }
                bodyClassName=""
            >
                {invoices.length === 0
                    ? <EmptyState icon={HiOutlineDocumentText} title="No invoices yet"
                                  description="Invoices appear here after your first billing period on a paid plan." />
                    : <InvoiceTable invoices={invoices} />}
            </SectionCard>
        </div>
    );
}
